import { Check, X, Zap, TrendingUp, Building2, Sparkles } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

/**
 * Plan tier definition
 */
interface PlanTier {
  name: string
  description: string
  price: number | 'custom'
  priceLabel?: string
  products: string
  searches: string
  icon: React.ComponentType<{ className?: string }>
  popular?: boolean
  features: {
    name: string
    included: boolean
    note?: string
  }[]
  cta: string
  ctaVariant?: 'default' | 'outline' | 'secondary'
}

/**
 * Plan tiers configuration
 */
const plans: PlanTier[] = [
  {
    name: 'Free',
    description: 'Perfect for trying out ShopFlare Search',
    price: 0,
    products: 'Up to 100 products',
    searches: '5,000 searches/month',
    icon: Zap,
    features: [
      { name: 'Fast autocomplete (<5ms)', included: true },
      { name: 'Category browsing', included: true },
      { name: 'Search analytics', included: true },
      { name: 'Dropdown widget', included: true },
      { name: 'Fullscreen widget', included: true },
      { name: 'Image proxy & caching', included: true },
      { name: 'Synonyms', included: false },
      { name: 'Searchandising rules', included: false },
      { name: 'Similar products API', included: false },
      { name: 'Priority support', included: false },
    ],
    cta: 'Current Plan',
    ctaVariant: 'outline',
  },
  {
    name: 'Starter',
    description: 'For small shops getting started',
    price: 19,
    products: 'Up to 1,000 products',
    searches: '50,000 searches/month',
    icon: Zap,
    features: [
      { name: 'Fast autocomplete (<5ms)', included: true },
      { name: 'Category browsing', included: true },
      { name: 'Search analytics', included: true },
      { name: 'Dropdown widget', included: true },
      { name: 'Fullscreen widget', included: true },
      { name: 'Image proxy & caching', included: true },
      { name: 'Synonyms', included: true },
      { name: 'Searchandising rules', included: false },
      { name: 'Similar products API', included: false },
      { name: 'Priority support', included: false },
    ],
    cta: 'Upgrade to Starter',
    ctaVariant: 'outline',
  },
  {
    name: 'Growth',
    description: 'For growing stores with more products',
    price: 39,
    products: 'Up to 5,000 products',
    searches: '200,000 searches/month',
    icon: TrendingUp,
    popular: true,
    features: [
      { name: 'Fast autocomplete (<5ms)', included: true },
      { name: 'Category browsing', included: true },
      { name: 'Search analytics', included: true },
      { name: 'Dropdown widget', included: true },
      { name: 'Fullscreen widget', included: true },
      { name: 'Image proxy & caching', included: true },
      { name: 'Synonyms', included: true },
      { name: 'Searchandising rules', included: true },
      { name: 'Similar products API', included: true },
      { name: 'Priority support', included: false },
    ],
    cta: 'Upgrade to Growth',
  },
  {
    name: 'Scale',
    description: 'For large catalogs and high traffic',
    price: 99,
    products: 'Up to 20,000 products',
    searches: '1,000,000 searches/month',
    icon: Building2,
    features: [
      { name: 'Fast autocomplete (<5ms)', included: true },
      { name: 'Category browsing', included: true },
      { name: 'Search analytics', included: true },
      { name: 'Dropdown widget', included: true },
      { name: 'Fullscreen widget', included: true },
      { name: 'Image proxy & caching', included: true },
      { name: 'Synonyms', included: true },
      { name: 'Searchandising rules', included: true },
      { name: 'Similar products API', included: true },
      { name: 'Priority support', included: true },
    ],
    cta: 'Upgrade to Scale',
    ctaVariant: 'outline',
  },
  {
    name: 'Enterprise',
    description: 'Custom solutions for large businesses',
    price: 'custom',
    priceLabel: 'Custom',
    products: 'Unlimited products',
    searches: 'Unlimited searches',
    icon: Sparkles,
    features: [
      { name: 'Everything in Scale', included: true },
      { name: 'Multi-store support', included: true },
      { name: 'Custom integrations', included: true },
      { name: 'Dedicated support', included: true },
      { name: 'SLA guarantee', included: true },
      { name: 'Custom analytics', included: true },
      { name: 'White-label option', included: true },
      { name: 'On-premise option', included: true },
    ],
    cta: 'Contact Sales',
    ctaVariant: 'outline',
  },
]

/**
 * Feature comparison data
 */
const featureComparison = {
  'Search Features': [
    { name: 'Fast autocomplete (<5ms)', free: true, starter: true, growth: true, scale: true },
    { name: 'Category browsing', free: true, starter: true, growth: true, scale: true },
    { name: 'Typo tolerance', free: 'Basic', starter: true, growth: true, scale: true },
    { name: 'Synonyms', free: false, starter: true, growth: true, scale: true },
    { name: 'Searchandising rules', free: false, starter: false, growth: true, scale: true },
    { name: 'Voice search', free: false, starter: false, growth: true, scale: true },
  ],
  'Product Discovery': [
    { name: 'Similar products API', free: false, starter: false, growth: true, scale: true },
    { name: 'Popular products', free: true, starter: true, growth: true, scale: true },
    { name: 'Promotional banners', free: false, starter: false, growth: true, scale: true },
    { name: 'Redirections', free: false, starter: true, growth: true, scale: true },
  ],
  'Analytics & Insights': [
    { name: 'Search analytics', free: true, starter: true, growth: true, scale: true },
    { name: 'Zero-result tracking', free: true, starter: true, growth: true, scale: true },
    { name: 'Click tracking', free: true, starter: true, growth: true, scale: true },
    { name: '365-day history', free: false, starter: false, growth: true, scale: true },
  ],
  'Support': [
    { name: 'Documentation', free: true, starter: true, growth: true, scale: true },
    { name: 'Email support', free: false, starter: true, growth: true, scale: true },
    { name: 'Priority support', free: false, starter: false, growth: false, scale: true },
    { name: 'Dedicated account manager', free: false, starter: false, growth: false, scale: 'Enterprise' },
  ],
}

/**
 * Plan card component
 */
function PlanCard({ plan }: { plan: PlanTier }) {
  const Icon = plan.icon

  return (
    <Card
      className={cn(
        'relative flex flex-col',
        plan.popular && 'border-primary ring-2 ring-primary/20'
      )}
    >
      {plan.popular && (
        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">
          Most Popular
        </Badge>
      )}

      <CardHeader className="text-center pb-2">
        <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
          <Icon className="h-6 w-6 text-primary" />
        </div>
        <CardTitle className="text-xl">{plan.name}</CardTitle>
        <CardDescription>{plan.description}</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col flex-1">
        {/* Price */}
        <div className="text-center mb-4">
          {plan.price === 'custom' ? (
            <div className="text-3xl font-bold">{plan.priceLabel}</div>
          ) : (
            <div>
              <span className="text-4xl font-bold">${plan.price}</span>
              <span className="text-muted-foreground">/month</span>
            </div>
          )}
        </div>

        {/* Limits */}
        <div className="text-center text-sm text-muted-foreground mb-4 space-y-1">
          <div>{plan.products}</div>
          <div>{plan.searches}</div>
        </div>

        <Separator className="my-4" />

        {/* Features */}
        <ul className="space-y-2 flex-1 mb-6">
          {plan.features.map((feature) => (
            <li key={feature.name} className="flex items-start gap-2 text-sm">
              {feature.included ? (
                <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              ) : (
                <X className="h-4 w-4 text-muted-foreground/50 shrink-0 mt-0.5" />
              )}
              <span className={cn(!feature.included && 'text-muted-foreground/50')}>
                {feature.name}
                {feature.note && (
                  <span className="text-muted-foreground"> ({feature.note})</span>
                )}
              </span>
            </li>
          ))}
        </ul>

        {/* CTA */}
        <Button
          variant={plan.ctaVariant || 'default'}
          className="w-full"
          disabled={plan.cta === 'Current Plan'}
        >
          {plan.cta}
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * Feature comparison table
 */
function FeatureComparisonTable() {
  return (
    <div className="mt-16">
      <h2 className="text-2xl font-semibold text-center mb-8">Feature Comparison</h2>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b">
              <th className="text-left py-3 px-4 font-medium">Feature</th>
              <th className="text-center py-3 px-4 font-medium">Free</th>
              <th className="text-center py-3 px-4 font-medium">Starter</th>
              <th className="text-center py-3 px-4 font-medium bg-primary/5">Growth</th>
              <th className="text-center py-3 px-4 font-medium">Scale</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(featureComparison).map(([category, features]) => (
              <>
                <tr key={category} className="bg-muted/50">
                  <td colSpan={5} className="py-2 px-4 font-medium text-sm">
                    {category}
                  </td>
                </tr>
                {features.map((feature) => (
                  <tr key={feature.name} className="border-b border-border/50">
                    <td className="py-2 px-4 text-sm">{feature.name}</td>
                    <td className="text-center py-2 px-4">
                      <FeatureValue value={feature.free} />
                    </td>
                    <td className="text-center py-2 px-4">
                      <FeatureValue value={feature.starter} />
                    </td>
                    <td className="text-center py-2 px-4 bg-primary/5">
                      <FeatureValue value={feature.growth} />
                    </td>
                    <td className="text-center py-2 px-4">
                      <FeatureValue value={feature.scale} />
                    </td>
                  </tr>
                ))}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * Feature value display (check, x, or text)
 */
function FeatureValue({ value }: { value: boolean | string }) {
  if (value === true) {
    return <Check className="h-4 w-4 text-primary mx-auto" />
  }
  if (value === false) {
    return <X className="h-4 w-4 text-muted-foreground/30 mx-auto" />
  }
  return <span className="text-xs text-muted-foreground">{value}</span>
}

/**
 * Plans Page
 *
 * Display pricing tiers and feature comparison
 */
export function PlansPage() {
  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="text-center max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold mb-2">Simple, Transparent Pricing</h1>
        <p className="text-muted-foreground">
          Get Doofinder-quality search at a fraction of the price.
          All plans include our lightning-fast autocomplete on Cloudflare's edge network.
        </p>
      </div>

      {/* Comparison callout */}
      <div className="flex justify-center">
        <Badge variant="secondary" className="text-sm px-4 py-1">
          Save 50-80% vs Doofinder ($49-$349/mo)
        </Badge>
      </div>

      {/* Plan Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {plans.map((plan) => (
          <PlanCard key={plan.name} plan={plan} />
        ))}
      </div>

      {/* Feature Comparison */}
      <FeatureComparisonTable />

      {/* FAQ Section */}
      <div className="mt-16 max-w-2xl mx-auto">
        <h2 className="text-2xl font-semibold text-center mb-8">Frequently Asked Questions</h2>

        <div className="space-y-6">
          <div>
            <h3 className="font-medium mb-2">How is search usage calculated?</h3>
            <p className="text-sm text-muted-foreground">
              Each autocomplete request counts as one search. Category browsing and product
              clicks are free. Unlike Doofinder's confusing "per product" model, our limits
              are simple monthly totals.
            </p>
          </div>

          <div>
            <h3 className="font-medium mb-2">What happens if I exceed my limits?</h3>
            <p className="text-sm text-muted-foreground">
              We'll notify you when you reach 80% of your limit. If you exceed it, search
              continues to work but you'll need to upgrade before the next billing cycle.
              We never cut off your customers mid-month.
            </p>
          </div>

          <div>
            <h3 className="font-medium mb-2">Can I switch plans anytime?</h3>
            <p className="text-sm text-muted-foreground">
              Yes! Upgrade instantly, downgrade at the end of your billing cycle.
              No contracts, cancel anytime.
            </p>
          </div>

          <div>
            <h3 className="font-medium mb-2">How fast is the search really?</h3>
            <p className="text-sm text-muted-foreground">
              Our autocomplete typically responds in 2-3ms thanks to Cloudflare KV edge
              storage. That's 10x faster than Doofinder's 25ms. Your customers see results
              instantly as they type.
            </p>
          </div>

          <div>
            <h3 className="font-medium mb-2">Do you support WooCommerce?</h3>
            <p className="text-sm text-muted-foreground">
              Yes! We sync directly with WooCommerce via REST API. Just add your store URL
              and API keys, and we'll import your products automatically. We also support
              custom post types and WPML.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
